// src/context/brandVoice/useBrandIdentity.js — the Brand Voice screen's THREE reads.
//
// GET /brand-identity/document and GET /context/brand-voice are two independent halves
// with two different backend gates (TenantAdmin vs AuthorizedTenant) and two different
// etags, so each one keeps its OWN `{ loading, refreshing, data, error }`: ⛔ neither may
// ever blank the other, and ⛔ their etags are never shared.
//
// ⭐ v190: the THIRD read is the ENGLISH voice (`readVoiceEn`). A 404 there is not an error —
// the workspace owns its Italian voice and no English file (the backend never falls back to
// the Italian) — so it resolves to `null`, "no English voice".
//
// ⭐ `refreshing` is what makes a re-read after a save (or after "Ricarica" / "Controlla
// di nuovo") non-destructive: `data` STAYS on screen until the answer lands, so the tab
// the user is looking at does not flash empty and the drafts keep something to sit on.
// A failed refresh therefore keeps the previous `data` and does NOT set `error` — an
// error banner replacing a whole tab because a background re-read blinked would be worse
// than the stale reading it removed. `error` is set only when there was nothing to keep.
//
// ⛔ Stale answers are dropped by a per-read sequence ticket, not by one shared flag: the
// two reads settle independently, and a slow answer is not always the older request (two
// "Controlla di nuovo" clicks in a row can resolve in either order).
import { useCallback, useEffect, useRef, useState } from 'react';
import { getBrandDocument } from '../brandIdentityApi.js';
import { getBrandVoice } from '../contextApi.js';

const PENDING = { loading: true, refreshing: false, data: null, error: null };

// ⭐ v190: the English voice. ⛔ It calls `getBrandVoice` itself instead of being a new
// contextApi export: the page suites mock contextApi with getBrandVoice / putBrandVoice only.
// A 404 → `null` ("no English voice"); anything else stays an error for `useReadPart`.
export const readVoiceEn = ({ tenant }) => Promise.resolve(getBrandVoice({ tenant, language: 'en' }))
  .catch((err) => {
    if (err?.status === 404 || err?.code === 'CONTEXT_ENTITY_NOT_FOUND') return null;
    throw err;
  });

// One read (document or voice) with its own state and its own sequence ticket.
// `read` must be a stable module-level function — it is a dependency of `run`.
function useReadPart(read, tenantId) {
  const [state, setState] = useState(PENDING);
  const seq = useRef(0);

  const run = useCallback((refresh) => {
    const ticket = seq.current + 1;
    seq.current = ticket;
    // Tenant change (or no tenant at all) → full reset, and the bumped ticket
    // discards whatever the previous workspace's read is about to answer.
    if (!tenantId) {
      setState(PENDING);
      return Promise.resolve(null);
    }
    setState((prev) => (refresh ? { ...prev, refreshing: true } : PENDING));
    let started;
    try {
      // Called synchronously (both reads must be in flight together), but a
      // synchronous throw — a mis-scoped call — still lands in the catch below.
      started = Promise.resolve(read({ tenant: tenantId }));
    } catch (error) {
      started = Promise.reject(error);
    }
    return started.then(
      (data) => {
        if (seq.current !== ticket) return null;
        setState({ loading: false, refreshing: false, data, error: null });
        return data;
      },
      (error) => {
        if (seq.current !== ticket) return null;
        setState((prev) => (prev.data
          ? { ...prev, loading: false, refreshing: false }
          : { loading: false, refreshing: false, data: null, error }));
        return null;
      },
    );
  }, [read, tenantId]);

  const load = useCallback(() => run(false), [run]);
  const reload = useCallback(() => run(true), [run]);
  useEffect(() => { load(); }, [load]);

  return [state, reload];
}

/**
 * @param {string|null} tenantId the ACTIVE workspace
 * @returns {{ doc: object, voice: object, voiceEn: object, reloadDoc: Function, reloadVoice: Function,
 *   reloadVoiceEn: Function, reloadAll: Function }}
 *   `reloadDoc` / `reloadVoice` / `reloadVoiceEn` resolve to the new data, or to `null` when the
 *   read refused (⛔ they never reject, so a caller's chain — "re-read, then clear the run
 *   lock" — is not broken by a refusal). ⚠️ `voiceEn.data === null` with no error = no English voice.
 */
export default function useBrandIdentity(tenantId) {
  const [doc, reloadDoc] = useReadPart(getBrandDocument, tenantId);
  const [voice, reloadVoice] = useReadPart(getBrandVoice, tenantId);
  const [voiceEn, reloadVoiceEn] = useReadPart(readVoiceEn, tenantId);

  // ⚠️ A voice save re-reads ALL THREE: the voice's own lock state lives in the document read,
  // and a first Italian save turns the English read from the platform's voice into a 404.
  const reloadAll = useCallback(
    () => Promise.all([reloadDoc(), reloadVoice(), reloadVoiceEn()])
      .then(([d, v, e]) => ({ doc: d, voice: v, voiceEn: e })),
    [reloadDoc, reloadVoice, reloadVoiceEn],
  );

  return { doc, voice, voiceEn, reloadDoc, reloadVoice, reloadVoiceEn, reloadAll };
}
